import { useState } from 'react';
import { Link, useParams } from 'react-router';
import type { eventWithTime } from '@rrweb/types';
import type { IssueSeverity, PublicAnalysisDto, PublicVisitDetailDto, PublicVisitSummaryDto, RecordingChunksDto } from '@daevio/shared';
import { FixBand } from '../../components/FixPrompt.tsx';
import { PublicShell } from '../../components/PublicShell.tsx';
import { RecordingTile } from '../../components/RecordingTile.tsx';
import { sessionsOnPage, worstPage } from '../../components/ScriptInvite.tsx';
import { ExitLanes, KeyPoints, MomentQuote, ProblemCards, ReportNav, Strengths, VerdictStats, reportLead, type LaneVisit, type ProblemView } from '../../components/Report.tsx';
import { SiteReview } from '../../components/SiteReview.tsx';
import { APP_PATH, signUpPath } from '../../lib/brand.ts';
import { REAL_USERS } from '../../lib/features.ts';
import { ownedTest } from '../../lib/ownerTokens.ts';
import { formatDuration, pathOf } from '../../lib/format.ts';
import { publicFetch, usePageMeta, usePublicQuery } from '../../lib/public.ts';
import { usePolling } from '../../lib/synthetic.ts';

/** The loaders of a public visit's steps and recording (no token: the slug is the key). */
function publicLoaders(slug: string, v: PublicVisitSummaryDto) {
  return {
    loadSteps: () => publicFetch<PublicVisitDetailDto>(`/analyses/${slug}/visits/${v.index}`).then((d) => d.steps),
    loadRecording: () => publicFetch<eventWithTime[]>(`/analyses/${slug}/visits/${v.index}/recording`),
    loadLive: (after: number) => publicFetch<RecordingChunksDto>(`/analyses/${slug}/visits/${v.index}/recording/live?after=${after}`),
  };
}

const SEVERITY_RANK: Record<IssueSeverity, number> = { high: 3, medium: 2, low: 1 };
const GAVE_UP = new Set(['abandoned', 'failed', 'blocked']);

function ShareButton() {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    const url = window.location.href.split('#')[0]!;
    try {
      if (navigator.share) {
        await navigator.share({ title: document.title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Share sheet dismissed or clipboard blocked: nothing to do.
    }
  };
  return (
    <button className="button" type="button" onClick={() => void share()}>
      {copied ? 'Link copied' : 'Share report'}
    </button>
  );
}

export function PublicReportPage() {
  const { slug } = useParams();
  const query = usePublicQuery<PublicAnalysisDto>(`/analyses/${slug}`);
  const a = query.data;
  const running = !!a && (a.status === 'running' || a.status === 'reporting');
  usePolling(!!a && (running || a.siteReview.status === 'pending'), query.reload, 3000);
  usePageMeta(a ? `${a.visitsTotal} AI visitors ${running ? 'are testing' : 'tested'} ${a.host}` : 'Test report', { noindex: true });
  const done = !!a && (a.status === 'completed' || a.status === 'cancelled');
  // The visit the report leads with: one that gave up, else the unhappiest; its last thought is the quote.
  const ended = (a?.visits ?? []).filter((v) => v.stepCount > 0 && v.status !== 'queued' && v.status !== 'running');
  const featured = done ? (ended.find((v) => GAVE_UP.has(v.status)) ?? [...ended].sort((x, y) => (x.finalMood ?? 11) - (y.finalMood ?? 11))[0]) : undefined;
  const featuredDetail = usePublicQuery<PublicVisitDetailDto>(featured && a ? `/analyses/${a.slug}/visits/${featured.index}` : null);

  if (query.error?.status === 404 || (!a && query.error)) {
    return (
      <PublicShell>
        <div className="public-section empty-state">
          <h1>{query.error.status === 404 ? 'This report does not exist' : 'The report could not be loaded'}</h1>
          <p>{query.error.status === 404 ? 'The link may be incomplete. Reports are reachable only through their full link.' : query.error.message}</p>
          <Link className="button primary" to="/">
            Test a site
          </Link>
        </div>
      </PublicShell>
    );
  }
  if (!a) {
    return (
      <PublicShell>
        <p className="muted public-section">Loading the analysis…</p>
      </PublicShell>
    );
  }

  const progress = a.visitsTotal > 0 ? Math.round((a.visitsFinished / a.visitsTotal) * 100) : 0;
  const mine = ownedTest(a.slug);
  const owner = !!mine;
  const inProject = mine?.projectId && mine.testId ? `/p/${mine.projectId}/synthetic/tests/${mine.testId}` : APP_PATH;
  const visitHref = (index: number, step?: number) => `/r/${a.slug}/v/${index + 1}${step !== undefined ? `?step=${step}` : ''}`;
  const reached = a.visits.filter((v) => v.status === 'converted' || v.status === 'satisfied').length;
  const lead = reportLead(a.report, `${reached} of ${a.visitsTotal} got what they came for.`);
  const lastStep = featuredDetail.data?.steps.at(-1);

  const lanes: LaneVisit[] = a.visits.map((v) => ({
    key: String(v.index),
    index: v.index,
    personaName: v.personaName,
    device: v.device,
    status: v.status,
    exit: v.exit,
    outcome: v.outcome,
    stepCount: v.stepCount,
    durationMs: v.durationMs,
    finalMood: v.finalMood,
    href: visitHref(v.index),
    endHref: visitHref(v.index, v.stepCount > 0 ? v.stepCount - 1 : undefined),
  }));

  const problems: ProblemView[] = (a.report?.groups ?? [])
    .filter((g) => g.issues.length > 0)
    .map((g) => {
      const visits = new Set(g.issues.map((i) => i.visitIndex));
      const first = [...g.issues].sort((x, y) => x.visitIndex - y.visitIndex || x.stepIndex - y.stepIndex)[0]!;
      return {
        key: g.title,
        title: g.title,
        severity: g.severity,
        description: g.description,
        fix: g.fix,
        hit: visits.size,
        total: a.visitsTotal,
        pages: [...new Set(g.issues.map((i) => pathOf(i.url).split('?')[0]!))],
        at: null,
        href: visitHref(first.visitIndex, first.stepIndex),
        watchLabel: `Watch ${first.personaName}, step ${first.stepIndex + 1}`,
        who: a.visits.filter((v) => visits.has(v.index)).map((v) => v.personaName),
      };
    })
    .sort((x, y) => y.hit - x.hit || SEVERITY_RANK[y.severity] - SEVERITY_RANK[x.severity]);

  const strengths = a.report?.strengths ?? [];
  const sections = [
    { id: 'exits', label: 'Where they ended up', count: a.visits.length },
    { id: 'problems', label: 'Problems', count: problems.length },
    ...(strengths.length > 0 ? [{ id: 'worked', label: 'What worked', count: strengths.length }] : []),
    { id: 'review', label: 'First impression' },
    { id: 'recordings', label: 'Recordings' },
    ...(a.report?.fixPrompt ? [{ id: 'fix', label: 'Fix prompt' }] : []),
  ];

  return (
    <PublicShell host={a.host}>
      <article className="r3-public" data-testid="public-report" data-status={a.status}>
        <header className="r3-head">
          <div className="r3-wrap">
            <div className="r3-hero-top">
              <span className="crumbs">
                <span className="here">Test report</span>
              </span>
              <div className="head-actions">
                {owner && (
                  <Link className="button" to={inProject}>
                    Open in your project
                  </Link>
                )}
                <ShareButton />
              </div>
            </div>
            <h1>
              {a.visitsTotal} AI visitors {running ? 'are testing' : 'tested'}{' '}
              <a href={a.siteUrl} target="_blank" rel="noreferrer nofollow">
                {a.host}
              </a>
            </h1>
            <p className="r3-goal">
              {a.mission ? (
                <>
                  <span>Their mission</span>
                  <strong>{a.mission.task}</strong>
                </>
              ) : (
                <span>They browsed freely, each with their own reason to visit.</span>
              )}
            </p>
            {a.previous && (
              <p className="notice">
                This is the re-run after fixes, with the same visitors and mission. <Link to={`/r/${a.previous.slug}`}>The first analysis</Link> found{' '}
                {a.previous.issues} {a.previous.issues === 1 ? 'issue' : 'issues'}
                {done ? `; this one found ${a.issues.length}.` : '.'}
              </p>
            )}
            {a.rerun && (
              <p className="notice">
                You ran it again after fixing: <Link to={`/r/${a.rerun.slug}`}>see the new analysis</Link>.
              </p>
            )}

            {running && (
              <section className="r3-progress" aria-label="Progress">
                {a.waiting === 'budget' && (
                  <p className="waiting-note" role="status">
                    High demand today: we reached today's limit of free analyses. Yours is in the queue and starts tomorrow morning. Keep this link to
                    come back to the report; we email you when it is ready.
                  </p>
                )}
                {a.waiting === 'busy' && (
                  <p className="waiting-note" role="status">
                    Many analyses are running right now. Yours is in the queue and starts in a few minutes.
                  </p>
                )}
                {a.waiting === 'preparing' && (
                  <p className="waiting-note" role="status">
                    Reading your site to give each visitor a concrete reason to be there. The visits start in a moment.
                  </p>
                )}
                <p>
                  <strong>{a.status === 'reporting' ? 'Writing the report…' : `${a.visitsFinished} of ${a.visitsTotal} visits done`}</strong>
                  {a.status === 'running' && !a.waiting && a.etaMs !== null && <span>, about {formatDuration(a.etaMs)} left</span>}
                </p>
                <span className="r3-progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
                  <span style={{ transform: `scaleX(${Math.max(progress, 4) / 100})` }} />
                </span>
                <p>You can leave this page open, or keep the link and come back: the report stays here.</p>
              </section>
            )}

            {done && a.report && (
              <>
                <h2 className="r3-verdict-line">{lead.headline}</h2>
                {lead.summary && <p className="r3-lede">{lead.summary}</p>}
                <KeyPoints points={lead.points} />
                <section className="r3-feature" aria-label="Verdict">
                  {featured && lastStep ? (
                    <MomentQuote
                      personaName={featured.personaName}
                      index={featured.index}
                      gaveUp={GAVE_UP.has(featured.status)}
                      thought={lastStep.thought}
                      path={pathOf(lastStep.url).split('?')[0]!}
                      clock={null}
                      href={visitHref(featured.index, Math.max(0, lastStep.index - 1))}
                      visitHref={visitHref(featured.index)}
                    />
                  ) : (
                    <div className="r3-moment" />
                  )}
                  <VerdictStats
                    visits={lanes}
                    problems={problems.length}
                    reached={{ n: reached, of: a.visitsTotal }}
                  />
                </section>
              </>
            )}
          </div>
        </header>

        {done && <ReportNav items={sections} />}

        <ExitLanes visits={lanes} behaviour="human" live={running} />

        {a.report && (
          <section id="problems" className="r3-section r3-section-alt" aria-label="Report">
            <div className="r3-wrap">
              <div className="r3-section-head">
                <div>
                  <h2>{problems.length === 0 ? 'No problems reported' : `${problems.length} ${problems.length === 1 ? 'problem' : 'problems'}, worst first.`}</h2>
                  <p>
                    {problems.length > 0
                      ? 'Each one shows how many visitors hit it, where, and the moment to watch.'
                      : 'The visitors did not report specific problems.'}
                  </p>
                </div>
              </div>
              {!a.report.headline && lead.points !== a.report.highlights && a.report.highlights.length > 0 && (
                <ul className="report-highlights">
                  {a.report.highlights.map((h) => (
                    <li key={h}>{h}</li>
                  ))}
                </ul>
              )}
              {problems.length > 0 && <ProblemCards problems={problems} />}
            </div>
          </section>
        )}

        {strengths.length > 0 && (
          <section id="worked" className="r3-section" aria-label="What worked" data-testid="strengths">
            <div className="r3-wrap">
              <div className="r3-section-head">
                <div>
                  <h2>What worked</h2>
                  <p>Keep these when you fix the rest.</p>
                </div>
              </div>
              <Strengths
                items={strengths.map((st) => ({
                  title: st.title,
                  description: st.description,
                  links: st.moments.flatMap((m) => {
                    const v = a.visits.find((x) => x.index === m.visitIndex);
                    return v ? [{ key: `${m.visitIndex}-${m.stepIndex}`, label: v.personaName, href: visitHref(m.visitIndex, m.stepIndex) }] : [];
                  }),
                }))}
              />
            </div>
          </section>
        )}

        <section id="review" className="r3-section r3-section-alt">
          <div className="r3-wrap">
            <SiteReview status={a.siteReview.status} review={a.siteReview.review} />
          </div>
        </section>

        {/* The recordings: the proof that real browsers opened the site. */}
        <section id="recordings" className="r3-section visitors" aria-label="The visitors">
          <div className="r3-wrap">
            <div className="r3-section-head">
              <div>
                <h2>{running ? 'Watch them live' : 'The recordings'}</h2>
                <p>
                  {a.visitsTotal} real browsers on {a.host}
                  {done ? '. Open one to watch it with their thoughts.' : ', a few seconds behind the visitors.'}
                </p>
              </div>
            </div>
            <ul className="rec-grid">
              {a.visits.map((v) => (
                <li key={v.index}>
                  <RecordingTile visit={v} href={visitHref(v.index)} {...publicLoaders(a.slug, v)} />
                </li>
              ))}
            </ul>
          </div>
        </section>

        {a.report?.fixPrompt && (
          <FixBand
            prompt={a.report.fixPrompt}
            title={problems.length > 1 ? `Fix all ${problems.length} with one prompt.` : 'Fix it with one prompt.'}
            lede="Paste it into Lovable, Cursor or Bolt. It lists the problems the visitors hit, worst first, and what to change."
            again={
              a.canRerun && owner ? (
                <Link className="button night-ghost" to={`${APP_PATH}/rerun/${a.slug}`}>
                  I fixed it, run again
                </Link>
              ) : undefined
            }
          />
        )}

        {done && (
          <section className="r3-section" aria-label="Next steps">
            <div className="r3-wrap r3-next">
              {/* One next step stands out: for the author, the re-run; for everyone else, their own site. */}
              {a.canRerun && owner ? (
                <div className="lead">
                  <h2>Fixed it? Run the same {a.visitsTotal} again.</h2>
                  <p>
                    Same personas, same mission and the same starting conditions, so the two reports compare. It uses {a.visitsTotal} of your visits.
                  </p>
                  <Link className="button" to={`${APP_PATH}/rerun/${a.slug}`}>
                    Run the same {a.visitsTotal} again
                  </Link>
                  {REAL_USERS && mine?.projectId && (
                    <p className="r3-next-real">
                      <Link to={sessionsOnPage(mine.projectId, worstPage(problems) ?? '/')}>
                        {worstPage(problems) ? `See if your real visitors get stuck on ${worstPage(problems)} too` : 'See how your real visitors do'}
                      </Link>
                    </p>
                  )}
                </div>
              ) : (
                <div className="lead">
                  <h2>Now your site.</h2>
                  <p>Three visitors with a persona open your site in a real browser and think out loud. Free with an account: 10 visits a month.</p>
                  <Link className="button" to="/">
                    Test my site
                  </Link>
                </div>
              )}
              <div>
                <h2>Keep it, share it, go further</h2>
                <p>Anyone with the link can open this report. It is not listed on search engines.</p>
                <div className="r3-next-actions">
                  <ShareButton />
                  <Link className="button" to="/#pricing">
                    Plans
                  </Link>
                  {!owner && (
                    <Link className="button" to={signUpPath()}>
                      Create a free account
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}
      </article>
    </PublicShell>
  );
}
